import { IDateRangeType } from '@entities/app';
import { ConfigProvider } from 'antd';
import ruRU from 'antd/lib/locale/ru_RU';
import React, { useEffect, useState } from 'react';
import { CookiesProvider, useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from 'styled-components';
import { setupAxiosInstance } from '@api/config';
import { getMe } from '@api/thunks/auth';
import { getLastClosetShift } from '@api/thunks/rKeeper';
import moment from './config/momentConfig';
import { AppDispatch, store } from '@config/store';
import ApplicationContext, { Theme } from './context/ApplicationContext';
import SidebarsContext, { useSidebarsContext } from './context/SidebarsContext';
import { authInfoSelect } from '@entities/auth';
import { rKeeperSelect } from '@entities/rKeeper';
import { MainRoutes } from './routes/MainRoutes';
import { colors, fonts } from '@styles';
import GlobalStyles from './styles/GlobalStyles';
import { IUser } from '@types';
import { customTheme } from '@utils/const';

const DATE_FORMAT = 'YYYY-MM-DD';

export const getTodayDateRange = () => {
    return {
        from: moment().utc().add(-1, 'month').format(DATE_FORMAT).valueOf(),
        to: moment().utc().format(DATE_FORMAT).valueOf(),
    };
};

const App: React.FC = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setupAxiosInstance(store);
    const { search } = window.location;
    const searchParams = new URLSearchParams(search);
    const { token, shop } = useSelector(authInfoSelect);
    const { lastShiftClosed } = useSelector(rKeeperSelect);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [theme, setTheme] = useState<Theme>('light');
    const [rangeStart, setRangeStart] = useState<string | undefined>(undefined);
    const [rangeEnd, setRangeEnd] = useState<string | undefined>(undefined);
    const [periodDate, setSetPeriodDate] = useState<string | undefined>(
        undefined
    );
    const location = useLocation();
    const path = location.pathname.split('/').slice(0, 3).join('/');
    const [inventGroups, setInventGroups] = useState<any>(undefined);
    const [lastShift, setLastShift] = useState<string | undefined>(undefined);
    const [dateFilterMain, setDateFilterMain] = useState<string | undefined>(
        () => {
            if (searchParams.get('date')) {
                return moment(searchParams.get('date'))
                    .format(DATE_FORMAT)
                    .valueOf();
            }
            return moment().format(DATE_FORMAT).valueOf();
        }
    );

    const [dateRange, setDateRange] = useState<IDateRangeType>(() => {
        return getTodayDateRange();
    });

    const { isActiveSubmenu, setActiveSubmenu, numActiveSubmenu } =
        useSidebarsContext();

    useEffect(() => {
        if (path === '/booking/form') {
            return;
        }
        const authTokens = localStorage.getItem('accessToken');

        if (!authTokens) {
            navigate('/login', { replace: true });
        }
        if (!token && authTokens) {
            dispatch(getMe());
        }
    }, [token, shop, dispatch, navigate, path]);

    useEffect(() => {
        if (!lastShiftClosed && shop) {
            dispatch(getLastClosetShift(shop.id));
        }
    }, [shop, lastShiftClosed, dispatch]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [cookies, setCookies, removeCookie] = useCookies<any['']>(['id']);

    return (
        <React.Fragment key="app">
            <CookiesProvider>
                <ConfigProvider theme={customTheme} locale={ruRU}>
                    <ThemeProvider theme={{ colors, fonts }}>
                        <ApplicationContext.Provider
                            value={{
                                theme,
                                setTheme: updateTheme => setTheme(updateTheme),

                                onLogin: (updatedUser: IUser) => {
                                    window.location.href = '/';
                                },
                                onLogout: () => {
                                    removeCookie('id');
                                    window.location.href = '/login';
                                },
                                rangeStart,
                                setRangeStart: (v: string | undefined) =>
                                    setRangeStart(v),
                                rangeEnd,
                                setRangeEnd: (v: string | undefined) =>
                                    setRangeEnd(v),
                                periodDate,
                                setSetPeriodDate: (v: string | undefined) =>
                                    setSetPeriodDate(v),
                                dateFilterMain,
                                setDateFilterMain: (v: string | undefined) =>
                                    setDateFilterMain(v),
                                dateRange,
                                setDateRange: (v: any) => setDateRange(v),
                                inventGroups,
                                setInventGroups: (v: any) => setInventGroups(v),
                                lastShift,
                                setLastShift: (v: any) => setLastShift(v),
                            }}
                        >
                            <SidebarsContext.Provider
                                value={{
                                    isActiveSubmenu,
                                    setActiveSubmenu: (v: boolean) =>
                                        setActiveSubmenu(v),
                                    numActiveSubmenu,
                                    setNumActiveSubmenu: (v: any) =>
                                        setActiveSubmenu(v),
                                }}
                            >
                                <GlobalStyles />

                                <ToastContainer style={{ width: '40%' }} />
                                <MainRoutes />
                            </SidebarsContext.Provider>
                        </ApplicationContext.Provider>
                    </ThemeProvider>
                </ConfigProvider>
            </CookiesProvider>
        </React.Fragment>
    );
};

export default App;
